<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2 class="deep-purple--text lighten-3">{{ $t('conglomerates.edit') }}</h2>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="deep-purple" class="mt-4" />

    <v-card v-if="!loading" class="pa-6 mt-4">
      <!-- Información del Conglomerado -->
      <v-row>
        <v-col cols="12">
          <h3 class="mb-4">{{ $t('conglomerates.conglomerate_name') }}</h3>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <span class="body black--text my-1">{{ $t('conglomerates.conglomerate_name') }}*</span>
          <v-text-field
            v-model="form.name"
            filled
            flat
            dense
          />
        </v-col>

        <v-col cols="12" md="4">
          <span class="body black--text my-1">{{ $t('conglomerates.country') }}*</span>
          <v-autocomplete
            v-model="form.countryId"
            :items="countries"
            item-value="id"
            item-text="name"
            filled
            flat
            dense
            hide-details
          />
        </v-col>

        <v-col cols="12" md="2">
          <span class="body black--text my-1">{{ $t('conglomerates.status') }}</span>
          <v-switch
            v-model="form.active"
            :label="form.active ? $t('conglomerates.active') : $t('conglomerates.inactive')"
            inset
          />
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <v-btn color="deep-purple" dark @click="saveConglomerate">
            {{ $t('common.save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Sedes Asociadas -->
    <v-card v-if="!loading" class="pa-6 mt-6">
      <v-row>
        <v-col cols="12">
          <h3 class="mb-4">{{ $t('conglomerates.associated_schools') }} ({{ schools.length }})</h3>
        </v-col>
      </v-row>

      <conglomerate-schools-table
        :schools="schools"
        :loading="loadingSchools"
        @unlink="handleUnlinkSchool"
      />
    </v-card>

    <!-- Acuerdos -->
    <v-card v-if="!loading" class="pa-6 mt-6">
      <v-row align="start">
      <v-col cols="12" md="6">
        <h3 class="mb-4 text-left">{{ $t('conglomerates.current_agreement') }}</h3>
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn text small color="deep-purple lighten-1" @click="toggleCreateArrangementModal">
            <v-icon size="21" class="mr-1">mdi-plus</v-icon>
            {{ $t('conglomerates.create_agreement') }}
          </v-btn>
        </v-col>
      </v-row>

      <conglomerate-arrangements-card
        :arrangements="arrangements"
        :loading="loadingArrangements"
        @create="toggleCreateArrangementModal"
        @edit="handleEditArrangement"
      />
    </v-card>

    <!-- Modals -->
    <create-arrangement-modal
      v-model="isCreateArrangementModalOpen"
      :school-group-id="conglomerateId"
      @created="loadArrangements"
    />

    <edit-arrangement-modal
      v-model="isEditArrangementModalOpen"
      :arrangement="selectedArrangement"
      @updated="loadArrangements"
    />
  </v-container>
</template>

<script>
import { COUNTRIES_CUSTOMER_ID } from '@/constants/data';
import ConglomerateSchoolsTable from '@/components/conglomerates/ConglomerateSchoolsTable.vue';
import ConglomerateArrangementsCard from '@/components/conglomerates/ConglomerateArrangementsCard.vue';
import CreateArrangementModal from '@/components/conglomerates/modals/CreateArrangementModal.vue';
import EditArrangementModal from '@/components/conglomerates/modals/EditArrangementModal.vue';
import conglomerateService from '@/services/conglomerate.service';
import schoolArrangementService from '@/services/schoolArrangement.service';
import countryService from '@/services/country.service';
import SnackbarMixin from '@/mixins/snackbar';
import LoadingMixin from '@/mixins/loading';

export default {
  name: 'ConglomerateEdition',
  mixins: [SnackbarMixin, LoadingMixin],
  components: {
    ConglomerateSchoolsTable,
    ConglomerateArrangementsCard,
    CreateArrangementModal,
    EditArrangementModal,
  },
  props: {
    conglomerateId: { type: [String, Number], required: true },
  },
  data: () => ({
    countries: [],
    form: {
      name: '',
      countryId: null,
      active: true,
    },
    schools: [],
    arrangements: [],
    loadingSchools: false,
    loadingArrangements: false,
    isCreateArrangementModalOpen: false,
    isEditArrangementModalOpen: false,
    selectedArrangement: null,
  }),
  async mounted() {
    await this.bootstrap();
  },
  methods: {
    async bootstrap() {
      this.loading = true;
      try {
        const countriesRes = await countryService.findAll();
        if (countriesRes?.data) {
          this.countries = countriesRes.data.filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
        }

        await Promise.all([
          this.loadConglomerate(),
          this.loadSchools(),
          this.loadArrangements(),
        ]);
      } catch (error) {
        this.snackbarError(this.$t('conglomerates.error_loading'));
      } finally {
        this.loading = false;
      }
    },
    async loadConglomerate() {
      const res = await conglomerateService.findOne(this.conglomerateId);
      if (res?.status === 'OK') {
        this.form = {
          name: res.content.name,
          countryId: res.content.countryId,
          active: res.content.active,
        };
      }
    },
    async loadSchools() {
      this.loadingSchools = true;
      try {
        const res = await conglomerateService.getSchools(this.conglomerateId);
        if (res?.status === 'OK') {
          this.schools = res.content;
        }
      } finally {
        this.loadingSchools = false;
      }
    },
    async loadArrangements() {
      this.loadingArrangements = true;
      try {
        const res = await schoolArrangementService.findAllBySchoolGroupId(this.conglomerateId);
        if (res?.status === 'OK') {
          this.arrangements = res.content;
        }
      } finally {
        this.loadingArrangements = false;
      }
    },
    async saveConglomerate() {
      if (!this.form.name || !this.form.countryId) {
        this.snackbarError(this.$t('conglomerates.error_validation'));
        return;
      }

      this.loading = true;
      try {
        const res = await conglomerateService.update(this.conglomerateId, {
          name: this.form.name,
          countryId: this.form.countryId,
          active: this.form.active,
        });

        if (res?.status === 'OK') {
          this.snackbarSuccess(this.$t('conglomerates.save_success'));
        } else {
          this.snackbarError(this.$t('conglomerates.error_saving'));
        }
      } catch (error) {
        this.snackbarError(this.$t('conglomerates.error_saving'));
      } finally {
        this.loading = false;
      }
    },
    async handleUnlinkSchool(schoolId) {
      try {
        const res = await conglomerateService.unlinkSchool(this.conglomerateId, schoolId);
        if (res?.status === 'OK') {
          this.snackbarSuccess(this.$t('conglomerates.unlink_success'));
          await this.loadSchools();
        }
      } catch (error) {
        this.snackbarError(this.$t('conglomerates.error_saving'));
      }
    },
    toggleCreateArrangementModal() {
      this.isCreateArrangementModalOpen = !this.isCreateArrangementModalOpen;
    },
    handleEditArrangement(arrangement) {
      this.selectedArrangement = arrangement;
      this.isEditArrangementModalOpen = true;
    },
  },
};
</script>
